import { MutableRefObject, RefObject, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import MapBox from '../../../components/map/MapBox.ts';
import { useMap } from '../../../components/map/useMap.ts';
import { useResizeObserver } from '../../../hooks/useResizeObserver.ts';
import { getFallbackCenterCoordinates } from '../../../utils/getFallbackCenterCoordinates.ts';
import { MapData, useMapCoords } from '../hooks/useMapCoords.ts';
import { Feature } from './types.ts';
import { useActiveProperty } from './useActiveProperty.ts';
import { useAddMapPins } from './useAddMapPins.ts';
import { useHandlePinClick } from './useHandlePinClick.ts';
import { useSetCursor } from './useSetCursor.ts';

const DEFAULT_ZOOM = 17;

export const useMLSMap = (): {
  mapRef: MutableRefObject<MapBox | null>;
  mapContainerRef: RefObject<HTMLDivElement>;
  activeProperty: MapData | undefined;
} => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);
  const mapData = useMapCoords();
  const subjectPinCoordinates = mapData.results.subject;
  const { studyID } = useParams();

  const handlePinClick = useHandlePinClick();

  // NOTE: This must run before the map is initialized.
  useEffect(() => {
    mapRef.current = null;
  }, [studyID]);
  // We don't want to run the above if we don't have to, most of the time
  // we'll just adjust the pins. But we do want to run it if the study changes
  // to make sure we reset.

  useMap({
    isEnabled: !mapData.isLoading,
    mapContainerRef,
    mapRef,
    center: subjectPinCoordinates ?? getFallbackCenterCoordinates(),
    zoom: DEFAULT_ZOOM,
    callbacks: {
      onPinClick: (feature?: Feature) => handlePinClick(feature),
    },
  });

  useAddMapPins(mapData, mapRef);

  useResizeObserver(mapContainerRef, () => {
    mapRef.current?.getMap()?.resize();
  });

  useSetCursor(mapRef.current?.getMap());

  const activeProperty = useActiveProperty(mapData);

  return { mapRef, mapContainerRef, activeProperty };
};
